import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  runTransaction,
  arrayUnion,
  increment,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";

// ─── QUEUE LISTENER ───────────────────────────────────────────────────────────
// Sorted client-side: most votes first, ties go to whoever asked first.
export function subscribeToQueue(venueId, callback) {
  return onSnapshot(collection(db, "venues", venueId, "queue"), (snapshot) => {
    const songs = snapshot.docs
      .map((d) => ({ ...d.data(), docId: d.id }))
      .sort((a, b) => {
        if ((b.votes || 0) !== (a.votes || 0)) return (b.votes || 0) - (a.votes || 0);
        const at = a.addedAt?.toMillis?.() ?? Number.MAX_SAFE_INTEGER;
        const bt = b.addedAt?.toMillis?.() ?? Number.MAX_SAFE_INTEGER;
        return at - bt;
      });
    callback(songs);
  });
}

// ─── NOW PLAYING LISTENER ─────────────────────────────────────────────────────
export function subscribeToNowPlaying(venueId, callback) {
  return onSnapshot(doc(db, "venues", venueId, "player", "nowPlaying"), (snap) => {
    callback(snap.exists() ? snap.data() : {});
  });
}

// ─── ADD SONG ─────────────────────────────────────────────────────────────────
export async function addSongToQueue(venueId, song, user) {
  return addDoc(collection(db, "venues", venueId, "queue"), {
    ...song,
    votes: 0,
    voters: {},
    addedBy: {
      uid: user.uid,
      displayName: user.displayName || "Guest",
      photoURL: user.photoURL || "",
    },
    addedAt: serverTimestamp(),
  });
}

// ─── VOTE ─────────────────────────────────────────────────────────────────────
// One vote per person per song, stored on the song. Tapping the same arrow again
// removes the vote; tapping the other arrow switches it. Runs in a transaction so
// two people voting at once can't overwrite each other.
export async function voteOnSong(venueId, docId, uid, direction) {
  const ref = doc(db, "venues", venueId, "queue", docId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    const voters = { ...(data.voters || {}) };
    const previous = voters[uid] || 0;
    const wanted = direction === "up" ? 1 : -1;
    const next = previous === wanted ? 0 : wanted;
    if (next === 0) delete voters[uid];
    else voters[uid] = next;
    tx.update(ref, { votes: (data.votes || 0) - previous + next, voters });
  });
}

// ─── REMOVE SONG ──────────────────────────────────────────────────────────────
export async function removeSong(venueId, docId) {
  await deleteDoc(doc(db, "venues", venueId, "queue", docId));
}

// ─── NOW PLAYING ──────────────────────────────────────────────────────────────
// Written by the venue's player. Everyone else reads it and animates the
// progress bar locally from positionMs, so we only write on real changes
// (new song, play, pause, seek), not every second.
export async function setNowPlaying(venueId, song, isPlaying, positionMs = 0, durationMs = 0) {
  await setDoc(doc(db, "venues", venueId, "player", "nowPlaying"), {
    song,
    isPlaying,
    positionMs,
    durationMs,
    skipVoters: [],
    updatedAt: serverTimestamp(),
  });
}

export async function updatePlayback(venueId, { isPlaying, positionMs, durationMs }) {
  await setDoc(
    doc(db, "venues", venueId, "player", "nowPlaying"),
    { isPlaying, positionMs, durationMs, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

// ─── SKIP VOTE ────────────────────────────────────────────────────────────────
export async function voteToSkip(venueId, uid) {
  await updateDoc(doc(db, "venues", venueId, "player", "nowPlaying"), {
    skipVoters: arrayUnion(uid),
  });
}

// ─── VIBE VOTE ────────────────────────────────────────────────────────────────
export function subscribeToVibeScore(venueId, callback) {
  return onSnapshot(doc(db, "venues", venueId, "vibe", "score"), (snap) => {
    if (snap.exists()) callback({ good: 0, bad: 0, ...snap.data() });
    else callback({ good: 0, bad: 0 });
  });
}

export async function submitVibeVote(venueId, type, previous) {
  const delta = { good: 0, bad: 0 };
  if (previous === "good") delta.good -= 1;
  if (previous === "bad") delta.bad -= 1;
  if (type === "good") delta.good += 1;
  if (type === "bad") delta.bad += 1;
  await setDoc(
    doc(db, "venues", venueId, "vibe", "score"),
    { good: increment(delta.good), bad: increment(delta.bad) },
    { merge: true }
  );
}
